import { Box, Button } from '@mui/material';
import { MRT_TableOptions, MRT_TableState } from 'material-react-table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { ThemedMaterialReactTable } from './ThemedMaterialReactTable';
import { LoadMoreProps } from './useLoadMore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Props<TData extends Record<string, any>> = MRT_TableOptions<TData> & LoadMoreProps<TData>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const InfiniteScrollReactTable = <TData extends Record<string, any> = {}>({
  rowCount,
  onLoadMore,
  hasMore,
  isLoading,
  isFetching,
  ...props
}: Props<TData>): React.JSX.Element => {
  const { t } = useTranslation();

  const loadMore = useCallback(() => {
    onLoadMore();
  }, [onLoadMore]);

  const [watchingRef] = useInfiniteScroll({
    loading: !!isFetching,
    hasNextPage: !!hasMore,
    onLoadMore: loadMore,
    // Optional: Margin when the watching element is in range
    rootMargin: '0px 0px 600px 0px',
    delayInMs: 0,
  });

  const state = useMemo<Partial<MRT_TableState<TData>>>(() => ({ ...props.state, isLoading }), [props.state, isLoading]);

  return (
    <div data-test-id="table-infinity-scroll" style={{ maxWidth: '100%' }}>
      <ThemedMaterialReactTable<TData> {...props} state={state} />
      {isFetching ||
        (!!hasMore && (
          <Box
            ref={watchingRef}
            sx={{
              textAlign: 'center',
              padding: 1,
            }}
          >
            <Button variant="text" onClick={loadMore}>
              {t('common:loadMoreData')}
            </Button>
          </Box>
        ))}
    </div>
  );
};
